import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { FileText, Loader2, Mail, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MonthField } from "@/components/DateField";
import { Panel } from "@/components/KpiCard";
import { api, download, errMsg, money } from "@/lib/api";

const moisCourant = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const Actions = ({ r, onDone }) => {
  const [busy, setBusy] = useState(null);
  const pdf = async () => {
    setBusy("pdf");
    try { await download("/recus-loyer/pdf", { mois: r.mois, locataire_id: r.locataire_id }, `Reçu de loyer ${r.mois}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const mail = async () => {
    if (!window.confirm(`Envoyer le reçu de ${r.mois} à ${r.locataire} (${r.courriel}) ? Une copie vous sera envoyée.`)) return;
    setBusy("mail");
    try { const { data } = await api.post("/recus-loyer/envoyer", { locataire_id: r.locataire_id, mois: r.mois }); toast.success(`Reçu envoyé à ${data.envoye_a}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="flex justify-end gap-1.5">
      <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={!!busy} onClick={pdf} data-testid={`recu-pdf-${r.locataire_id}`}>
        {busy === "pdf" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}Reçu PDF
      </Button>
      <Button variant="outline" size="icon" className="h-7 w-7" disabled={!!busy || !r.courriel} onClick={mail}
        title={r.courriel ? "Envoyer le reçu par courriel" : "Ajoutez le courriel du locataire dans Locataires mémorisés"} data-testid={`recu-envoyer-${r.locataire_id}`}>
        {busy === "mail" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
      </Button>
    </div>
  );
};

const Ligne = ({ r, onDone }) => (
  <tr className="border-b border-slate-100" data-testid={`recu-${r.locataire_id}`}>
    <td className="py-2 px-2 font-mono text-xs">{r.numero}</td>
    <td className="py-2 px-2">{r.locataire}</td>
    <td className="py-2 px-2 text-slate-600">{r.logement}</td>
    <td className="py-2 px-2 font-mono text-xs text-slate-500">{r.depots.map((x) => x.date).join(", ")}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{money(r.recu)}</td>
    <td className="py-2 px-2"><span className={`rounded px-1.5 py-0.5 text-[11px] ${r.complet ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.complet ? "Payé" : "Partiel"}</span>
      {r.envoye_le && <span className="ml-1.5 text-[10px] font-mono text-slate-500" data-testid={`recu-envoye-${r.locataire_id}`}>envoyé {r.envoye_le.slice(0, 10)}</span>}</td>
    <td className="py-2 px-2"><Actions r={r} onDone={onDone} /></td>
  </tr>
);

export const RecusLoyerPanel = ({ version }) => {
  const [mois, setMois] = useState(moisCourant());
  const [data, setData] = useState(null);
  const [cale, setCale] = useState(false);
  const [busy, setBusy] = useState(false);
  const charger = useCallback(() => {
    if (!mois) return;
    api.get("/recus-loyer", { params: { mois } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [mois]);
  useEffect(() => { charger(); }, [charger, version]);
  useEffect(() => {
    if (cale || !data?.derniere_operation) return;
    setCale(true);
    const m = data.derniere_operation.slice(0, 7);
    if (m < mois) setMois(m);
  }, [data, cale, mois]);
  const tous = async () => {
    setBusy(true);
    try { await download("/recus-loyer/pdf", { mois }, `Reçus de loyer ${mois}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Reçus de loyer" testId="recus-loyer-panel" action={
      <Button variant="outline" size="sm" disabled={busy || !data?.recus.length} onClick={tous} data-testid="recus-loyer-tous">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Receipt className="h-4 w-4 mr-1.5" />}Tous les reçus du mois (PDF)
      </Button>}>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs text-slate-500 flex flex-col gap-1">Mois de loyer<MonthField value={mois} onChange={(v) => { setCale(true); setMois(v); }} className="w-40" data-testid="recus-loyer-mois" /></label>
        <p className="text-xs text-slate-500 pb-2">Un reçu par locataire ayant payé ce mois (un paiement fait à partir du 25 peut compter pour le mois suivant).</p>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : data.recus.length === 0
        ? <p className="text-sm text-slate-500" data-testid="recus-loyer-vide">Aucun loyer reçu pour ce mois parmi les locataires mémorisés.</p>
        : (
          <div className="overflow-x-auto">
            <SmartTable id="recus-loyer" className="w-full text-sm" data-testid="recus-loyer-table">
              <thead><tr className="border-b border-slate-200 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-2">N°</th><th className="py-2 px-2">Locataire</th><th className="py-2 px-2">Logement</th><th className="py-2 px-2">Paiements</th>
                <th className="py-2 px-2 text-right">Reçu</th><th className="py-2 px-2">Statut</th><th className="py-2 px-2 text-right">Reçu au locataire</th>
              </tr></thead>
              <tbody>{data.recus.map((r) => <Ligne key={r.locataire_id} r={r} onDone={charger} />)}</tbody>
            </SmartTable>
          </div>
        )}
    </Panel>
  );
};
